/**
 * タイトル画面(docs/02_game_design.md §1)。
 *
 * **2段構成**: 起動して最初の1回だけ、タイトル絵のスプラッシュ(`TitleSplash`)を全面に出す。
 * クリック / タップ / キーで従来のメニュー(`TitleMenu`)へ遷移アニメーションで切り替わる。
 * 2回目以降(プレイから戻ってきたとき等)はスプラッシュを飛ばして直接メニューを出す
 * (`sessionStorage` に「見た」印。タブを閉じるまで有効)。
 *
 * メニュー部は docs/15_game_feel.md §1-1 / §1-2 / §1-5 の「Typing Land 式」に寄せてある:
 *
 * 1. **ロゴが遊び**: `PALTOWN` の7タイルを p-a-l-t-o-w-n と打つと1文字ずつ色づいてポップし、
 *    そろうと星がバーストする。打たなくてもボタンで進めるので**強制ではない**
 * 2. **キー操作ファースト**: Enter =「はじめる」、数字キー = その他の入口。
 *    ボタンには同じ文字のキーバッジを添える(バッジは `aria-hidden` = ボタン名は変えない)
 * 3. **順番ポップイン**: タイル → サブタイトル → ボタン列の順に 40ms ずつずらして入場。
 *    transform / opacity だけ(Chromebook 30fps 要件)で、**入場中もクリックは効く**
 *    (誤爆防止のための入力ブロックはしない = 既存の E2E をそのまま通す)
 *
 * 演出の部品は `ui/components/gameFeel.tsx`(まなび・ステージ選択と共通)を使う。
 */

import { useEffect, useRef, useState } from 'react';
import { canonicalOrigin, shouldShowMoveNotice } from '../logic/canonicalOrigin';
import { advanceLogoTyping, LOGO_WORD } from '../logic/logoTyping';
import {
  isTextEntryTarget,
  TITLE_DIGIT_ACTIONS,
  TITLE_DIGIT_KEYS,
  TITLE_KEY_BADGES,
  type TitleAction,
} from '../logic/titleKeys';
import { sound } from '../services/sound';
import { useAuthStore } from '../store/authStore';
import { useGameStore } from '../store/gameStore';
import { KeyBadge, popInDelay, StarBurst, useChoiceKeys, useHotkey } from './components/gameFeel';
import { ProfileBar } from './components/ProfileBar';

const LOGO_LETTERS = [...LOGO_WORD];

/** ポップインの並び順(docs/15 §1-5)。タイル7枚 → サブタイトル → ボタン3つ。 */
const SUB_POP_INDEX = LOGO_LETTERS.length;
const BUTTON_POP_INDEX = SUB_POP_INDEX + 1;
/** ボタンまで確実に順番どおり出したいので、既定(320ms)より上限を上げる。 */
const POP_MAX_MS = 520;

/** キー操作の行き先。`gameStore` の最新値をイベント時点で読む。 */
function runTitleAction(action: TitleAction): void {
  const state = useGameStore.getState();
  switch (action) {
    case 'start':
      state.goModeSelect();
      return;
    case 'puzzle':
      state.goPuzzle();
      return;
    case 'learn':
      state.goLearn();
      return;
    case 'gacha':
      void state.goGacha();
      return;
    case 'avatar':
      void state.goAvatar();
      return;
    case 'myPage':
      void state.goMyPage();
      return;
    case 'pal':
      state.goPal();
      return;
  }
}

/**
 * スプラッシュを見たかどうか(タブ単位)。メニュー操作を1クリック余計にさせないため、
 * プレイ等から戻ってきた2回目以降は飛ばす。E2E はテスト冒頭で reload するものが多く、
 * 「マウント時に印を付ける」ことで reload 後は従来どおりメニューが直接出る。
 */
const SPLASH_SEEN_KEY = 'paltyping.titleSplash.v1';

function hasSeenSplash(): boolean {
  try {
    return sessionStorage.getItem(SPLASH_SEEN_KEY) === '1';
  } catch {
    return false;
  }
}

function markSplashSeen(): void {
  try {
    sessionStorage.setItem(SPLASH_SEEN_KEY, '1');
  } catch {
    // プライベートモード等で保存できないときは、毎回スプラッシュが出るだけ。
  }
}

/** スプラッシュ画像の置き場(原本は design/title/。`node design/title/export.mjs` で再生成)。 */
function splashSrc(name: string): string {
  return `${import.meta.env.BASE_URL}assets/img/title/${name}.webp`;
}

/** 退場アニメーション(styles.css の splash-leave)の長さ+少しの余白。 */
const SPLASH_LEAVE_MS = 560;

/**
 * タイトル絵のスプラッシュ(修正・参考/タイトル画面_参考.jpeg のレイアウト)。
 * 背景以外は「下からフワッと入場 → ふわふわ漂う」の2段アニメーション
 * (入場は外側の `.splash-item`、ループは内側の img。1要素に重ねると後勝ちで消えるため)。
 * クリック / タップ / 任意キーで `onDismiss`。
 */
function TitleSplash({ leaving, onDismiss }: { leaving: boolean; onDismiss: () => void }) {
  // キーでも進める(docs/15 §1-2 キー操作ファースト)。修飾キー付きはブラウザに譲る。
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.defaultPrevented || event.repeat) return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (isTextEntryTarget(event.target)) return;
      onDismiss();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onDismiss]);

  return (
    <div
      className={`title-splash ${leaving ? 'leaving' : ''}`}
      role="button"
      aria-label="クリック か タップ で すすむ"
      onClick={onDismiss}
    >
      {/* 背景だけは動かさない(全面絵。動くと酔う)。 */}
      <img className="splash-bg" src={splashSrc('bg')} alt="" draggable={false} />
      <div className="splash-item splash-logo">
        <img src={splashSrc('logo')} alt="PaLTown パルタウン" draggable={false} />
      </div>
      <div className="splash-item splash-octo">
        <img src={splashSrc('octo')} alt="" draggable={false} />
      </div>
      <div className="splash-item splash-pal">
        <img src={splashSrc('pal')} alt="" draggable={false} />
      </div>
      <div className="splash-item splash-poka">
        <img src={splashSrc('poka')} alt="" draggable={false} />
      </div>
      <div className="splash-item splash-prompt">
        <span className="splash-prompt-pill">クリック か タップ で はじめよう!</span>
      </div>
    </div>
  );
}

export function TitleScreen() {
  /**
   * splash: スプラッシュ表示中(メニューは出さない = キー入力もスプラッシュに専念)
   * leaving: スプラッシュが退場中。下でメニューがポップインを始める
   * menu: 従来のタイトルメニューだけ
   */
  const [phase, setPhase] = useState<'splash' | 'leaving' | 'menu'>(() =>
    hasSeenSplash() ? 'menu' : 'splash',
  );

  useEffect(() => {
    if (phase === 'menu') return;
    markSplashSeen();
    // ここでは掃除しない(印はタブが生きているあいだ残す)。
  }, [phase]);

  // 退場アニメーションが終わったらスプラッシュを片付ける。
  // reduced-motion でアニメが無くても確実に進むよう、animationend でなくタイマーで送る。
  useEffect(() => {
    if (phase !== 'leaving') return;
    const timer = window.setTimeout(() => setPhase('menu'), SPLASH_LEAVE_MS);
    return () => window.clearTimeout(timer);
  }, [phase]);

  // setState の更新関数に副作用(音)を入れない(StrictMode で二重に呼ばれる)。
  const dismissSplash = (): void => {
    if (phase !== 'splash') return;
    sound.unlock();
    sound.play('ui');
    setPhase('leaving');
  };

  return (
    <div className="screen title">
      {/* 退場の裏でメニューがポップインを始める = これ自体が画面遷移アニメーション。 */}
      {phase === 'splash' ? null : <TitleMenu />}
      {phase === 'menu' ? null : (
        <TitleSplash leaving={phase === 'leaving'} onDismiss={dismissSplash} />
      )}
    </div>
  );
}

/**
 * 旧URL(`*.pages.dev`)で遊んでいる人への引っ越し案内(docs/03_architecture.md §8.1)。
 *
 * 匿名(ゲスト)のままだと進捗はブラウザごとに分かれてしまうので、
 * **先に「とうろく」してから** 新しい場所でログインしてもらう順番を伝える。
 * 正式URLと同じオリジンで見ているとき・localhost・`dev.paltown.app` では出ない。
 */
function MoveNotice() {
  const target = canonicalOrigin();
  // メールを持たない子は「ひきつぎコード」で移る(docs/10 §3.4)。
  // とうろくの仕組みが無いローカルモードでは押しても行き先が無いので出さない。
  const authSupported = useAuthStore((auth) => auth.supported);
  const goAccount = useGameStore((state) => state.goAccount);
  return (
    <div className="move-notice">
      <p className="move-notice-text">
        あたらしい ばしょに ひっこしたよ! さきに「とうろく」か「ひきつぎコード」で うつしてから、
        あたらしい ばしょで ログイン(または コードを いれる)してね
      </p>
      {authSupported ? (
        <button type="button" className="move-notice-button" onClick={() => goAccount('transfer')}>
          ひきつぎコードを つくる
        </button>
      ) : null}
      {/* 同じタブで開く(子どもがタブを見失わないように)。 */}
      <a className="move-notice-link" href={target}>
        あたらしい ばしょへ →
      </a>
    </div>
  );
}

function TitleMenu() {
  const goModeSelect = useGameStore((state) => state.goModeSelect);
  // 思考力パズル(docs/11_thinking_module.md §5-2「タウンなしの暫定導線」)。
  const goPuzzle = useGameStore((state) => state.goPuzzle);
  // まなびモード(docs/10_paltown_platform.md §2)。パズルと同じ暫定導線。
  const goLearn = useGameStore((state) => state.goLearn);

  /** 旧URLで遊んでいるか(引っ越し案内の出し分け)。オリジンは動かないので初回だけ見る。 */
  const [legacyHost] = useState(shouldShowMoveNotice);

  /** そろっているタイルの数。 */
  const [typed, setTyped] = useState(0);
  /** 直前にポップさせたタイル。同じタイルを打ち直しても掛け直せるよう連番を添える。 */
  const [pop, setPop] = useState<{ readonly index: number; readonly seq: number } | null>(null);
  /** 星バーストの世代。増えるたびにバーストを出し直す。 */
  const [burst, setBurst] = useState(0);
  // イベントハンドラから読む用(setState の更新関数を副作用に使わないため。StrictMode 対策)。
  const typedRef = useRef(0);
  const seqRef = useRef(0);

  // Enter =「はじめる」。ボタンにフォーカスがあるときは `useHotkey` が譲る(二重発火の防止)。
  useHotkey('Enter', goModeSelect);
  // 数字キー = その他の入口。ボタンのキーバッジと同じ並び。
  useChoiceKeys(TITLE_DIGIT_KEYS, (index) => {
    const action = TITLE_DIGIT_ACTIONS[index];
    if (action) runTitleAction(action);
  });

  // ── ロゴのタイルタイピング(docs/15 §1-1)──
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      // 押しっぱなしの連射・修飾キー付き(ブラウザのショートカット)・
      // 数字キー等で処理済みのものは見ない。
      if (event.defaultPrevented || event.repeat) return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (isTextEntryTarget(event.target)) return;

      const result = advanceLogoTyping(typedRef.current, event.key);
      if (result.hit === 'ignore') return;
      typedRef.current = result.typed;
      setTyped(result.typed);
      sound.unlock();
      if (result.hit === 'advance' || result.hit === 'restart') {
        seqRef.current += 1;
        setPop({ index: result.typed - 1, seq: seqRef.current });
        // 打ち間違いには音を出さない(叱らない)。当たったときだけ小さな報酬。
        sound.play(result.completed ? 'clear' : 'correct');
        if (result.completed) setBurst((generation) => generation + 1);
      } else {
        setPop(null);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const complete = typed >= LOGO_LETTERS.length;

  return (
    <>
      <div className="title-logo">
        {/* タイルそのものがロゴなので、読み上げには1枚の絵として渡す。 */}
        <div className={`logo-tiles ${complete ? 'complete' : ''}`} role="img" aria-label="PaLTown">
          {LOGO_LETTERS.map((letter, index) => (
            <span
              key={letter + String(index)}
              className="logo-slot gf-pop-in"
              style={popInDelay(index, 40, POP_MAX_MS)}
            >
              <span
                // ポップは打つたびに掛け直したいので、key に連番を混ぜて作り直させる。
                key={pop?.index === index ? `pop-${String(pop.seq)}` : 'idle'}
                className={`logo-tile c${String(index)} ${index < typed ? 'on' : 'off'} ${
                  pop?.index === index ? 'pop' : ''
                }`}
              >
                {letter}
              </span>
            </span>
          ))}
          {/* そろったときの星バースト。タイル列の中心に重ねる(0px の錨)。 */}
          <span className="logo-burst-anchor" aria-hidden="true">
            <StarBurst key={burst} stars={complete ? 4 : 0} radius={165} />
          </span>
        </div>
        <span className="title-sub gf-pop-in" style={popInDelay(SUB_POP_INDEX, 40, POP_MAX_MS)}>
          たのしく タイピング!
        </span>
      </div>
      <div className="title-buttons">
        <button
          type="button"
          className="big-button start gf-pop-in"
          style={popInDelay(BUTTON_POP_INDEX, 40, POP_MAX_MS)}
          onClick={goModeSelect}
        >
          はじめる
          <KeyBadge keyLabel={TITLE_KEY_BADGES.start} className="on-dark" />
        </button>
        <button
          type="button"
          className="big-button puzzle gf-pop-in"
          style={popInDelay(BUTTON_POP_INDEX + 1, 40, POP_MAX_MS)}
          onClick={goPuzzle}
        >
          🧩 パズル
          <KeyBadge keyLabel={TITLE_KEY_BADGES.puzzle} className="on-dark" />
        </button>
        <button
          type="button"
          className="big-button learn gf-pop-in"
          style={popInDelay(BUTTON_POP_INDEX + 2, 40, POP_MAX_MS)}
          onClick={goLearn}
        >
          📚 まなび
          <KeyBadge keyLabel={TITLE_KEY_BADGES.learn} className="on-dark" />
        </button>
      </div>
      {/* プロフィール欄のボタンにも同じ様式のキーバッジを出す(タイトルのときだけ)。 */}
      <ProfileBar keyHints />
      {/* 旧URLのときだけ。「とうろく」ボタン(ProfileBar)のすぐ下に置く。 */}
      {legacyHost ? <MoveNotice /> : null}
      <p className="tips">
        ロゴを <b>PALTOWN</b> と うってみよう! <b>Enter</b> で はじめる、すう字キーでも えらべるよ。
      </p>
    </>
  );
}
